/**
 * Created by maciej on 22/06/2016.
 */
import * as moment from 'moment';
import 'moment-timezone';
import {app} from '../app';
import IPromise = angular.IPromise;
import Duration = moment.Duration;

export interface ISessionService {
    isReady:boolean;
    activeSession:ISession;
    answers:IAnswer[];

    createSession(questionnaire:number, upid:string, raterGuid:string, exitRedirect:string);
    finishSession(status?:string);
    saveAnswer(answer:IAnswer, persistFlow:boolean):ng.IHttpPromise<any>;
    onSaveAnswer(callback:(answer:IAnswer) => void):() => void;
    getAnswer(questionId:number):IAnswer;
    resumeSession(id:number, upid:string, raterGuid:string):IPromise<any>;

    ready(callback:() => void);
    sessionResumed();
    sessionPaused();
    onSessionPaused(callback:() => void):() => void;
    onSessionResumed(callback:() => void):() => void;

    setExitRedirectURL(url: string);
    exitRedirect();
}

export class SessionService implements ISessionService {

    private http:ng.IHttpService;
    private q:ng.IQService;
    private window:ng.IWindowService;
    private store:angular.a0.storage.INamespacedStoreService;
    private translate:angular.translate.ITranslateService;
    private luminaEnv:any;
    private returnUrlSanitise:any;

    private saveAnswerCallbacks:((answer:IAnswer) => void)[] = [];
    private readyCallbacks:(() => void)[]                    = [];
    private sessionResumedCallbacks:(() => void)[]           = [];
    private sessionPausedCallbacks:(() => void)[]            = [];

    private exitRedirectURL:string;

    private _isReady:boolean = false;
    public activeSession:ISession;
    public answers:IAnswer[] = [];

    constructor($http:ng.IHttpService,
                $q:ng.IQService,
                $window:ng.IWindowService,
                store:angular.a0.storage.INamespacedStoreService,
                $translate:angular.translate.ITranslateService,
                luminaEnv:any,
                returnUrlSanitise:any) {
        this.http              = $http;
        this.q                 = $q;
        this.window            = $window;
        this.store             = store;
        this.translate         = $translate;
        this.luminaEnv         = luminaEnv;
        this.returnUrlSanitise = returnUrlSanitise;
    }

    set isReady(ready:boolean) {
        this._isReady = true;
        if (ready) {
            while (this.readyCallbacks.length > 0) {
                this.readyCallbacks.pop()();
            }
        }
    }

    get isReady():boolean {
        return this._isReady;
    }

    /**
     * Runs callback if session is set to be ready. If session is not ready callback is delayed and run after it is set
     * to be ready. Normally readiness of the session is managed by the SessionController.
     *
     * @param callback
     */
    public ready(callback:() => void) {
        if (this._isReady) {
            callback();
            return;
        }

        this.readyCallbacks.push(callback);
    }

    public createSession(questionnaire:number, upid:string, raterGuid:string, exitRedirect:string) {
        let context:{
            impersonated?:boolean;
            impersonator?:any;
            previous_questionnaire?:boolean;
            locale?:string;
            rate?:string;
            raterGuid?:string;
            exit_redirect?:string
        } = {};

        if (raterGuid) {
            context.raterGuid = raterGuid;
        }

        if (this.store.get('profile').impersonated) {
            context.impersonated = true;
            context.impersonator = this.store.get('profile').impersonator;
        }

        if (exitRedirect) {
            context.exit_redirect = exitRedirect;
        }

        const previousQuestionnaire = this.store.get('previous_questionnaire');
        if (previousQuestionnaire) {
            context.previous_questionnaire = previousQuestionnaire;
            this.store.remove('previous_questionnaire');
        }

        context.locale = this.translate.storage().get(this.translate.storageKey());

        return this.http.post(
            '/api/questionnaire/' + questionnaire + '/session',
            JSON.stringify({
                context : context,
                timezone: moment.tz.guess(),
                upid    : upid
            })
        );
    }

    public finishSession(status = 'finished') {
        return this.http.put(
            '/api/session/' + this.activeSession.id,
            JSON.stringify({
                status  : status,
                timezone: this.activeSession.timezone
            })
        );
    }

    saveAnswer(answer:IAnswer, persistFlow:boolean):ng.IHttpPromise<any> {
        if (this.activeSession.id == null) {
            return null;
        }

        this.saveAnswerCallbacks.forEach(callback => callback(answer));

        let data = {
            question_id : answer.questionId,
            displayed_at: answer.displayedAt,
            answered_at : answer.answeredAt,
            time        : answer.time.asMilliseconds(),
            values      : answer.values
        };

        if (persistFlow) {
            data['flow'] = 'persist';
        }

        return this.http.post(
            '/api/session/' + this.activeSession.id + '/question/' + answer.questionId,
            JSON.stringify(data)
        ).then((data) => {
            if (data.status !== 200) {
                return this.q.reject(data);
            }
            this.addAnswer(answer);
            return this.q.resolve(data);
        }, (data) => {
            return this.q.reject(data);
        });
    }

    private addAnswer(answer:IAnswer) {
        // essential !
        const answerCopy = angular.copy(answer);

        for (let index in this.answers) {
            if (this.answers[index].questionId == answer.questionId) {
                this.answers[index] = answerCopy;
                return;
            }
        }

        this.answers.push(answerCopy);
    }

    public getAnswer(questionId:number):IAnswer {
        for (let answer of this.answers) {
            if (answer.questionId == questionId) {
                return answer;
            }
        }

        return null;
    }

    public resumeSession(id:number, upid:string, raterGuid:string):IPromise<any> {
        raterGuid = raterGuid ? '/' + raterGuid : '';

        return this.http({
            method: 'GET',
            url   : '/api/questionnaire/' + id + '/lastSession/' + upid + raterGuid
        })
            .then((value) => {
                if (value.status !== 200) { // @TODO: why it enters it if the error is 404 ??
                    return this.q.reject();
                }

                const session = value.data['session'];

                this.activeSession = {
                    id             : session['id'],
                    context        : session['context'],
                    timezone       : session['timezone'],
                    questionnaireId: value.data['questionnaire']['id']
                };

                this.answers = value.data['answers'].map((answer) => {
                    return {
                        questionId : answer['question_id'],
                        displayedAt: moment(answer['displayed_at'], 'Y-m-d H:i:s'),
                        answeredAt : moment(answer['answered_at'], 'Y-m-d H:i:s'),
                        time       : moment.duration(answer['time']),
                        values     : answer['values']
                    };
                });

                return this.q.resolve(session);
            });
    }

    public sessionResumed() {
        if (!this.activeSession) {
            return;
        }

        this.sessionResumedCallbacks.forEach((callback) => callback());
    }

    public sessionPaused() {
        if (!this.activeSession) {
            return;
        }

        this.sessionPausedCallbacks.forEach((callback) => callback());
    }

    public onSaveAnswer(callback:(answer:IAnswer) => void):() => void {
        let index = this.saveAnswerCallbacks.push(callback) - 1;
        return () => this.saveAnswerCallbacks.splice(index, 1);
    }

    public onSessionResumed(callback:() => void):() => void {
        let index = this.sessionResumedCallbacks.push(callback) - 1;
        return () => this.sessionResumedCallbacks.splice(index, 1);
    }

    public onSessionPaused(callback:() => void):() => void {
        let index = this.sessionPausedCallbacks.push(callback) - 1;
        return () => this.sessionPausedCallbacks.splice(index, 1);
    }

    setExitRedirectURL(url: string) {
        this.exitRedirectURL = url;
    }

    public exitRedirect() {
        let ssoVersion = this.store.get('ssoVersion');
        let defaultSsoUrl = ssoVersion && ssoVersion == '2' ? this.luminaEnv.ssoV2Url : this.luminaEnv.ssoUrl;
        let sanitisedUrl = this.returnUrlSanitise(this.exitRedirectURL, defaultSsoUrl);
        this.window.location.href = sanitisedUrl;
    }

    static factory() {
        const factory = ($http:ng.IHttpService,
                         $q:ng.IQService,
                         $window:ng.IWindowService,
                         store:angular.a0.storage.INamespacedStoreService,
                         $translate:angular.translate.ITranslateService,
                         luminaEnv,
                         returnUrlSanitise) => new SessionService($http, $q, $window, store, $translate, luminaEnv, returnUrlSanitise);

        factory.$inject = ['$http', '$q', '$window', 'store', '$translate', 'luminaEnv', 'returnUrlSanitise'];
        return factory;
    }
}

app.factory('SessionService', SessionService.factory());